import { type ImgHTMLAttributes } from "react";

import primaryAsset from "@/assets/brand/primary.png.asset.json";
import horizontalAsset from "@/assets/brand/horizontal.png.asset.json";
import iconAsset from "@/assets/brand/icon.png.asset.json";
import iconMonoAsset from "@/assets/brand/icon-mono.png.asset.json";
import footerAsset from "@/assets/brand/footer.png.asset.json";

/**
 * Peopels logo system. One source of truth for every brand placement.
 *
 * Variant → use case:
 *   primary     → auth, helper onboarding, hero brand moments (icon + wordmark + slogan)
 *   horizontal  → site header, transactional emails (icon + wordmark, no slogan)
 *   icon        → favicons, social profile pictures, badges (icon only, full colour)
 *   monochrome  → embedded in dark-on-light surfaces where colour would clash
 *   footer      → site footer (dark navy lockup, white wordmark + slogan)
 *
 * All assets are PNGs served from the Lovable CDN with strong caching. They
 * scale crisply on retina because the source files are 2x the largest in-app
 * render size (640w primary, 645w horizontal, 149w icon).
 */
export type PeopelsLogoVariant =
  | "primary"
  | "horizontal"
  | "icon"
  | "monochrome"
  | "footer";

const VARIANTS: Record<
  PeopelsLogoVariant,
  { src: string; width: number; height: number; alt: string }
> = {
  primary: {
    src: primaryAsset.url,
    width: 900,
    height: 255,
    alt: "Peopels — For the people, by the Peopels",
  },
  horizontal: {
    src: horizontalAsset.url,
    width: 900,
    height: 190,
    alt: "Peopels",
  },
  icon: {
    src: iconAsset.url,
    width: 350,
    height: 260,
    alt: "Peopels",
  },
  monochrome: {
    src: iconMonoAsset.url,
    width: 100,
    height: 122,
    alt: "Peopels",
  },
  footer: {
    src: footerAsset.url,
    width: 415,
    height: 135,
    alt: "Peopels — For the people, by the Peopels",
  },
};

type Props = Omit<
  ImgHTMLAttributes<HTMLImageElement>,
  "src" | "alt" | "width" | "height"
> & {
  variant?: PeopelsLogoVariant;
  /** Render height in CSS px. Width is derived from the asset aspect ratio so the mark never distorts. */
  height?: number;
  /** Override alt text. Defaults to a context-appropriate string per variant. */
  alt?: string;
  /** Render as decoration (alt=""). Use when the logo sits next to a visible "Peopels" wordmark elsewhere on the page. */
  decorative?: boolean;
};

export function PeopelsLogo({
  variant = "horizontal",
  height,
  alt,
  decorative,
  className,
  style,
  ...rest
}: Props) {
  const v = VARIANTS[variant];
  const renderHeight = height ?? defaultHeight(variant);
  const renderWidth = Math.round((v.width / v.height) * renderHeight);

  return (
    <img
      src={v.src}
      // 2x the rendered size for retina sharpness; the source asset is large
      // enough to satisfy this without an upscaled blur.
      width={renderWidth}
      height={renderHeight}
      alt={decorative ? "" : (alt ?? v.alt)}
      aria-hidden={decorative ? true : undefined}
      decoding="async"
      // Brand mark is above the fold (header/footer); eager for header, lazy for footer is handled at the call site.
      loading={rest.loading ?? "eager"}
      draggable={false}
      className={className}
      style={{
        // intrinsic ratio so layout doesn't shift before the PNG decodes
        height: renderHeight,
        width: "auto",
        ...style,
      }}
      {...rest}
    />
  );
}

function defaultHeight(variant: PeopelsLogoVariant): number {
  switch (variant) {
    case "primary":
      return 56;
    case "horizontal":
      return 32;
    case "icon":
      return 40;
    case "monochrome":
      return 32;
    case "footer":
      return 56;
  }
}
